<template>
  <card class="stacked-form" :title="isEditMode ? $t('tools.updateTool') : $t('tools.createTool')" :on-cancel="cancel">
    <Form :validation-schema="schema" @submit="saveOrUpdateTool">
      <div class="row">
        <!-- Left Column -->
        <div class="col-md-6">
          <!-- Name -->
          <div class="mb-3">
            <material-input-field
              id="name"
              v-model="name"
              :label="`${$t('common.name')} *`"
              :placeholder="$t('common.name')"
              name="name"
              variant="static"
            />
          </div>

          <!-- Description -->
          <div class="mb-3">
            <material-input-field
              id="description"
              v-model="description"
              :label="`${$t('common.description')} *`"
              :placeholder="$t('common.description')"
              name="description"
              variant="static"
            />
          </div>

          <!-- Serial/SKU -->
          <div class="mb-3">
            <material-input-field
              id="sku"
              v-model="sku"
              label="Serial"
              placeholder="Serial"
              name="sku"
              variant="static"
            />
          </div>

          <!-- Location -->
          <div class="mb-3">
            <label>{{ $t('locations.location') }} *</label>
            <el-select
              v-model="locationOption"
              filterable
              :class="selectClass"
              size="large"
              :placeholder="$t('tools.selectLocation')"
              class="w-100"
            >
              <el-option
                v-for="item in locationOptions"
                :key="item.id"
                :value="item.address"
                :label="item.address"
              />
            </el-select>
          </div>

          <!-- Tool Type -->
          <div class="mb-3">
            <label>{{ $t('tools.selectType') }} *</label>
            <div v-if="toolTypeOption !== 'New Type'">
              <el-select
                v-model="toolTypeOption"
                filterable
                :class="selectClass"
                size="large"
                :placeholder="$t('common.selectType')"
                class="w-100"
              >
                <el-option
                  v-for="item in toolTypeOptions"
                  :key="item.id"
                  :value="item.type"
                  :label="item.type"
                />
                <el-option value="New Type">{{ $t('common.addNewType') }}</el-option>
              </el-select>
            </div>
            <div v-else class="d-flex align-items-center gap-2">
              <div class="flex-grow-1">
                <material-input-field
                  id="newToolType"
                  v-model="newToolType"
                  :placeholder="$t('tools.newToolType')"
                  name="newToolType"
                  variant="static"
                />
              </div>
              <el-button type="danger" circle size="small" @click="cancelNewToolType">
                <el-icon><Close /></el-icon>
              </el-button>
            </div>
          </div>

          <!-- Daily Price / Cost -->
          <template v-if="!enableToolPriceTypes">
            <div class="mb-3">
              <material-input-field
                id="dailyPrice"
                v-model="dailyPrice"
                :label="$t('tools.dailyPrice')"
                :placeholder="$t('tools.dailyPrice')"
                name="dailyPrice"
                type="number"
                variant="static"
              />
            </div>
          </template>
          <template v-else>
            <div class="mb-3">
              <material-input-field
                id="dailyPrice"
                v-model="dailyPrice"
                :label="$t('tools.cost')"
                :placeholder="$t('tools.cost')"
                name="dailyPrice"
                type="number"
                variant="static"
              />
            </div>
            <div class="mb-3">
              <label>{{ $t('tools.costType') }}</label>
              <el-select
                v-model="toolPriceTypeOption"
                :class="selectClass"
                size="large"
                :placeholder="toolPriceTypePlaceholder"
                class="w-100"
              >
                <el-option
                  v-for="item in toolPriceTypeOptions"
                  :key="item.id"
                  :value="item.id"
                  :label="item.type"
                />
              </el-select>
            </div>
          </template>

          <!-- Isle -->
          <div class="mb-3">
            <material-input-field
              id="isle"
              v-model="isle"
              :label="$t('tools.isle')"
              :placeholder="$t('tools.isle')"
              name="isle"
              variant="static"
            />
          </div>

          <!-- Shelf -->
          <div class="mb-3">
            <material-input-field
              id="shelf"
              v-model="shelf"
              :label="$t('tools.shelf')"
              :placeholder="$t('tools.shelf')"
              name="shelf"
              variant="static"
            />
          </div>
        </div>

        <!-- Right Column -->
        <div class="col-md-6">
          <!-- Image Upload (Desktop) -->
          <div v-if="!isMobile" class="mb-3 image-sizing">
            <image-upload
              :show-upload-button="false"
              :src="toolImageSrc"
              :select-text="$t('image.selectImage')"
              @change="handleFileUpload"
            />
          </div>

          <!-- Brand -->
          <div class="mb-3">
            <material-input-field
              id="brandName"
              v-model="brandName"
              :label="$t('tools.brand')"
              :placeholder="$t('tools.brand')"
              name="brandName"
              variant="static"
            />
          </div>

          <!-- ERP Item Code -->
          <div class="mb-3">
            <material-input-field
              id="erpItemCode"
              v-model="erpItemCode"
              :label="$t('tools.erpItemCode')"
              :placeholder="$t('tools.erpItemCode')"
              name="erpItemCode"
              variant="static"
            />
          </div>

          <!-- Max Reservation Days -->
          <div class="mb-3">
            <material-input-field
              id="maxReservationDays"
              v-model="maxReservationDays"
              :label="$t('tools.maxReservationDays')"
              :placeholder="$t('tools.maxReservationDays')"
              name="maxReservationDays"
              type="number"
              variant="static"
            />
          </div>

          <!-- Device Id -->
          <div v-if="enableGPS" class="mb-3">
            <material-input-field
              id="deviceId"
              v-model="deviceId"
              :label="$t('tools.deviceId')"
              :placeholder="$t('tools.deviceId')"
              name="deviceId"
              variant="static"
            />
          </div>

          <!-- Device Type -->
          <div v-if="enableGPS" class="mb-3">
            <label>{{ $t('tools.deviceType') }}</label>
            <el-select
              v-model="deviceTypeOption"
              filterable
              clearable
              :class="selectClass"
              size="large"
              :placeholder="$t('tools.selectDeviceType')"
              class="w-100"
            >
              <el-option
                v-for="item in deviceTypeOptions"
                :key="item.id"
                :value="item.id"
                :label="item.type"
              />
            </el-select>
          </div>

          <!-- Purchase Date -->
          <div class="mb-3">
            <label>{{ $t('tools.purchaseDate') }}</label>
            <el-date-picker
              v-model="purchaseDate"
              type="date"
              :placeholder="$t('tools.purchaseDate')"
              format="DD/MM/YYYY"
              value-format="YYYY-MM-DD"
              :clearable="true"
              class="w-100"
            />
          </div>

          <!-- Purchase Price -->
          <div class="mb-3">
            <material-input-field
              id="purchasePrice"
              v-model="purchasePrice"
              :label="$t('tools.purchasePrice')"
              :placeholder="$t('tools.purchasePrice')"
              name="purchasePrice"
              type="number"
              variant="static"
            />
          </div>

          <!-- Ask for tool image on return (per-tool override) -->
          <div class="mb-3">
            <div class="form-check">
              <input
                id="askForToolImageOnReturnForToolReservations"
                v-model="askForToolImageOnReturnForToolReservations"
                type="checkbox"
                class="form-check-input"
              />
              <label class="form-check-label" for="askForToolImageOnReturnForToolReservations">
                {{ $t('customerSettings.askForToolImageOnReturnForToolReservations') }}
              </label>
            </div>
            <div v-if="askForToolImageOnReturnForToolReservations" class="mt-2" style="max-width:240px">
              <label class="form-label text-sm">{{ $t('customerSettings.askForToolImageOnReturnForToolReservationsAfterDays') }}</label>
              <input
                v-model.number="askForToolImageOnReturnForToolReservationsAfterDays"
                type="number"
                min="1"
                step="1"
                class="form-control form-control-sm"
                @blur="clampAskForToolImageDays"
              />
            </div>
          </div>

          <!-- Multi-Tool Checkbox -->
          <div class="mb-3">
            <div class="form-check">
              <input
                id="multiTool"
                v-model="multiTool"
                type="checkbox"
                class="form-check-input"
              />
              <label class="form-check-label" for="multiTool">
                {{ $t('tools.multiTool') }}
              </label>
            </div>
          </div>

          <!-- Total Count (if multi-tool) -->
          <div v-if="multiTool" class="mb-3">
            <material-input-field
              id="totalCount"
              v-model="totalCount"
              :label="$t('tools.totalCount')"
              :placeholder="$t('tools.totalCount')"
              name="totalCount"
              type="number"
              variant="static"
            />
          </div>

          <!-- Image Upload (Mobile) -->
          <div v-if="isMobile" class="mb-3 image-sizing">
            <image-upload
              :show-upload-button="false"
              :src="toolImageSrc"
              :select-text="$t('image.selectImage')"
              @change="handleFileUpload"
            />
          </div>

          <!-- Custom QR Banner Checkbox -->
          <div v-if="enableCustomQRBanner" class="mb-3">
            <div class="form-check">
              <input
                id="showQRBannerUpload"
                v-model="showQRBannerUpload"
                type="checkbox"
                class="form-check-input"
              />
              <label class="form-check-label" for="showQRBannerUpload">
                {{ $t('tools.customQRBanner') }}
              </label>
            </div>
          </div>

          <!-- QR Banner Upload -->
          <div v-if="showQRBannerUpload" class="mb-3 image-sizing">
            <hr v-if="isMobile" />
            <image-upload
              :show-upload-button="false"
              :src="bannerSrc"
              :select-text="$t('image.selectImage')"
              @change="handleQRBannerFileUpload"
            />
          </div>
        </div>

        <!-- Reminders (calibration, expiry etc.) — create only; edited from the tool page reminders pill -->
        <div v-if="enableToolReminders && !isEditMode" class="col-12 mt-2">
          <hr />
          <div class="d-flex align-items-center justify-content-between">
            <label class="mb-0">{{ $t('tools.toolReminders') }}</label>
            <button type="button" class="btn btn-sm btn-outline-dark mb-0" @click="addReminder">
              {{ $t('tools.addReminder') }}
            </button>
          </div>
          <p class="text-sm text-muted mb-2">{{ $t('tools.reminderAdminsAlwaysNotified') }}</p>
          <div v-for="(reminder, index) in reminders" :key="index" class="border rounded p-3 mb-2">
            <div class="row">
              <div class="col-md-4 mb-2">
                <label class="form-label text-sm">{{ $t('tools.reminderLabel') }} *</label>
                <input
                  v-model="reminder.label"
                  type="text"
                  class="form-control form-control-sm"
                  :placeholder="$t('tools.reminderLabel')"
                />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label text-sm">{{ $t('tools.reminderDate') }} *</label>
                <el-date-picker
                  v-model="reminder.dueDate"
                  type="date"
                  :placeholder="$t('tools.reminderDate')"
                  format="DD/MM/YYYY"
                  value-format="YYYY-MM-DD"
                  :clearable="true"
                  class="w-100"
                />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label text-sm">{{ $t('tools.reminderExtraRecipients') }}</label>
                <el-select
                  v-model="reminder.recipientUserIds"
                  multiple
                  filterable
                  clearable
                  :class="selectClass"
                  :placeholder="$t('tools.reminderExtraRecipients')"
                  class="w-100"
                >
                  <el-option
                    v-for="u in userOptions"
                    :key="u.id"
                    :value="u.id"
                    :label="`${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.email ? ` (${u.email})` : '')"
                  />
                </el-select>
              </div>
              <div class="col-md-1 mb-2 d-flex align-items-end justify-content-end">
                <el-button type="danger" circle size="small" @click="removeReminder(index)">
                  <el-icon><Close /></el-icon>
                </el-button>
              </div>
            </div>
            <div class="d-flex flex-wrap gap-3">
              <div class="form-check">
                <input
                  :id="`notifyMonthBefore-${index}`"
                  v-model="reminder.notifyMonthBefore"
                  type="checkbox"
                  class="form-check-input"
                />
                <label class="form-check-label" :for="`notifyMonthBefore-${index}`">
                  {{ $t('tools.notifyMonthBefore') }}
                </label>
              </div>
              <div class="form-check">
                <input
                  :id="`notifyWeekBefore-${index}`"
                  v-model="reminder.notifyWeekBefore"
                  type="checkbox"
                  class="form-check-input"
                />
                <label class="form-check-label" :for="`notifyWeekBefore-${index}`">
                  {{ $t('tools.notifyWeekBefore') }}
                </label>
              </div>
              <div class="form-check">
                <input
                  :id="`notifyOnDate-${index}`"
                  v-model="reminder.notifyOnDate"
                  type="checkbox"
                  class="form-check-input"
                />
                <label class="form-check-label" :for="`notifyOnDate-${index}`">
                  {{ $t('tools.notifyOnDate') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="col-12 d-flex justify-content-end mt-3">
          <button
            type="submit"
            class="btn mb-0 btn-dark"
            :disabled="isLoading"
          >
            <span v-if="isLoading">
              <i class="fas fa-spinner fa-spin me-2"></i>
              {{ $t('common.saving') || 'Saving...' }}
            </span>
            <span v-else>
              {{ $t('common.submit') }}
            </span>
          </button>
        </div>
      </div>
    </Form>
  </card>
</template>

<script>
import { Form } from 'vee-validate';
import * as yup from 'yup';
import api from "../../services/api";
import { getApiErrorMessage } from "../../utils/apiError";
import { ElDatePicker, ElSelect, ElOption, ElButton, ElIcon } from 'element-plus';
import { Close } from '@element-plus/icons-vue';
import ImageUpload from '../../components/ImageUpload.vue';
import MaterialInputField from '../../components/MaterialInputField.vue';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import Card from "../../components/Card.vue";

export default {
  name: 'CreateEditToolForm',
  components: {
    Form,
    ElDatePicker,
    ElSelect,
    ElOption,
    ElButton,
    ElIcon,
    Close,
    ImageUpload,
    MaterialInputField,
    Card
  },
  props: {
    // If toolId is provided the form is in edit mode
    toolId: {
      type: [Number, String],
      default: null
    },
    // Pre-fetched tool data for edit mode (avoids a redundant API call)
    toolData: {
      type: Object,
      default: null
    },
    // When true, the form creates a new tool pre-filled from toolData
    isDuplicate: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'refresh-data', 'refresh-tooltypesdata', 'navigate-to-tool'],
  data() {
    return {
      showQRBannerUpload: false,
      toolTypeOptions: [],
      locationOptions: [],
      toolTypeOption: "",
      locationOption: "",
      toolQRCodeBanner: null,
      name: "",
      description: "",
      newToolType: "",
      toolImageBytes: "",
      purchaseDate: new Date().toISOString().slice(0, 10),
      brandName: "",
      dailyPrice: "",
      sku: "",
      maxReservationDays: "",
      askForToolImageOnReturnForToolReservations: false,
      askForToolImageOnReturnForToolReservationsAfterDays: 1,
      isle: "",
      shelf: "",
      erpItemCode: "",
      purchasePrice: "",
      multiTool: false,
      totalCount: "",
      isMobile: window.innerWidth <= 1024,
      isLoading: false,
      enableCustomQRBanner: false,
      enableGPS: false,
      enableToolPriceTypes: false,
      toolPriceTypeOption: 1,
      toolPriceTypePlaceholder: '',
      toolPriceTypeOptions: [],
      deviceId: "",
      deviceTypeOption: null,
      deviceTypeOptions: [],
      enableToolReminders: false,
      reminders: [],
      userOptions: [],
    };
  },
  computed: {
    isEditMode() {
      return this.toolId !== null && this.toolId !== undefined && !this.isDuplicate;
    },
    schema() {
      return yup.object({
        name: yup.string().required(this.$t('errors.nameEmpty')),
        description: yup.string().required(this.$t('errors.descriptionEmpty')),
        sku: yup.string().nullable(),
        newToolType: this.toolTypeOption === 'New Type'
          ? yup.string().required(this.$t('errors.newToolTypeEmpty'))
          : yup.string().nullable(),
        dailyPrice: yup.number().nullable().transform((value, originalValue) =>
          originalValue === '' ? null : value
        ).typeError(this.$t('errors.mustBeNumber')),
        isle: yup.string().nullable(),
        shelf: yup.string().nullable(),
        brandName: yup.string().nullable(),
        erpItemCode: yup.string().nullable(),
        maxReservationDays: yup.number().nullable().transform((value, originalValue) =>
          originalValue === '' ? null : value
        ).typeError(this.$t('errors.mustBeNumber')),
        purchasePrice: yup.number().nullable().transform((value, originalValue) =>
          originalValue === '' ? null : value
        ).typeError(this.$t('errors.mustBeNumber')),
        totalCount: this.multiTool
          ? yup.number().required(this.$t('errors.totalCountEmpty')).positive().integer()
          : yup.number().nullable().transform((value, originalValue) =>
              originalValue === '' ? null : value
            ),
      });
    },
    toolImageSrc() {
      return this.toolImageBytes ? 'data:image/png;base64,' + this.toolImageBytes : null;
    },
    bannerSrc() {
      return this.toolQRCodeBanner ? 'data:image/png;base64,' + this.toolQRCodeBanner : null;
    },
    selectClass() {
      const darkMode = localStorage.getItem('darkMode');
      return darkMode === 'true' ? 'select-primary' : 'select-standard';
    }
  },
  watch: {
    showQRBannerUpload(newValue) {
      if (newValue === false) {
        this.toolQRCodeBanner = null;
      }
    },
    toolData(newVal) {
      if (newVal) {
        this.populateFromToolData(newVal);
      }
    }
  },
  created() {
    this.toolPriceTypePlaceholder = this.$t('toolPriceType.day');
    this.toolPriceTypeOptions = [
      { id: 1, type: this.$t('toolPriceType.day') },
      { id: 2, type: this.$t('toolPriceType.hour') },
      { id: 3, type: this.$t('toolPriceType.kilometer') },
      { id: 4, type: this.$t('toolPriceType.meter') },
    ];

    getCustomerConfig('EnableCustomQRBanner').then(config => {
      this.enableCustomQRBanner = config === '' ? false : config;
    });
    getCustomerConfig('EnableGPS').then(config => {
      this.enableGPS = config === '' ? false : config;
    });
    getCustomerConfig('EnableToolPriceTypes').then(config => {
      this.enableToolPriceTypes = config === '' ? false : config;
    });
    getCustomerConfig('EnableToolReminders').then(config => {
      this.enableToolReminders = config === '' ? false : config;
    });

    // Populate fields from toolData if in edit or duplicate mode
    if ((this.isEditMode || this.isDuplicate) && this.toolData) {
      this.populateFromToolData(this.toolData);
    }
  },
  mounted() {
    this.fetchFormData();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
    },

    clampAskForToolImageDays() {
      const n = Number(this.askForToolImageOnReturnForToolReservationsAfterDays);
      if (!Number.isFinite(n) || n < 1) {
        this.askForToolImageOnReturnForToolReservationsAfterDays = 1;
      } else {
        this.askForToolImageOnReturnForToolReservationsAfterDays = Math.floor(n);
      }
    },

    populateFromToolData(tool) {
      this.name = tool.name || '';
      this.description = tool.description || '';
      this.sku = tool.sku || '';
      this.brandName = tool.brandName || '';
      this.erpItemCode = tool.erpItemCode || '';
      this.dailyPrice = tool.dailyPrice != null ? String(tool.dailyPrice) : '';
      this.isle = tool.isle || '';
      this.shelf = tool.shelf || '';
      this.maxReservationDays = tool.maxReservationDays ? String(tool.maxReservationDays) : '';
      this.askForToolImageOnReturnForToolReservations = tool.askForToolImageOnReturnForToolReservations === true;
      this.askForToolImageOnReturnForToolReservationsAfterDays = tool.askForToolImageOnReturnForToolReservationsAfterDays && tool.askForToolImageOnReturnForToolReservationsAfterDays >= 1 ? tool.askForToolImageOnReturnForToolReservationsAfterDays : 1;
      this.purchaseDate = tool.purchaseDate ? tool.purchaseDate.split('T')[0] : '';
      this.purchasePrice = tool.purchasePrice != null ? String(tool.purchasePrice) : '';
      this.multiTool = tool.multiTool || false;
      this.totalCount = tool.totalCount ? String(tool.totalCount) : '';
      this.toolImageBytes = tool.toolImageBytes || '';
      this.locationOption = tool.location ? tool.location.address : '';
      this.toolTypeOption = tool.toolType ? tool.toolType.type : '';
      this.toolPriceTypeOption = tool.toolPriceTypeId || 1;
      this.deviceId = tool.deviceId || '';
      this.deviceTypeOption = tool.deviceTypeId || null;
      // Duplicated tools copy the reminders as new rows
      this.reminders = (tool.reminders || []).map(r => ({
        label: r.label || '',
        dueDate: r.dueDate ? r.dueDate.split('T')[0] : '',
        notifyMonthBefore: r.notifyMonthBefore || false,
        notifyWeekBefore: r.notifyWeekBefore || false,
        notifyOnDate: r.notifyOnDate || false,
        recipientUserIds: r.recipientUserIds || []
      }));
      if (tool.toolQRCodeBanner) {
        this.toolQRCodeBanner = tool.toolQRCodeBanner;
        this.showQRBannerUpload = true;
      }
    },

    addReminder() {
      this.reminders.push({
        label: '',
        dueDate: '',
        notifyMonthBefore: false,
        notifyWeekBefore: false,
        notifyOnDate: true,
        recipientUserIds: []
      });
    },

    removeReminder(index) {
      this.reminders.splice(index, 1);
    },

    cancelNewToolType() {
      if (this.toolTypeOptions.length > 0) {
        this.toolTypeOption = this.toolTypeOptions[0].type;
      } else {
        this.toolTypeOption = '';
      }
    },

    cancel() {
      this.$emit('close');
    },

    handleFileUpload(file) {
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          let binary = '';
          const bytes = new Uint8Array(reader.result);
          bytes.forEach((byte) => binary += String.fromCharCode(byte));
          this.toolImageBytes = btoa(binary);
        };
        reader.readAsArrayBuffer(file);
      } else {
        // In edit mode, revert to original image if user clears
        this.toolImageBytes = this.toolData ? (this.toolData.toolImageBytes || '') : '';
      }
    },

    handleQRBannerFileUpload(file) {
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          let binary = '';
          const bytes = new Uint8Array(reader.result);
          bytes.forEach((byte) => binary += String.fromCharCode(byte));
          this.toolQRCodeBanner = btoa(binary);
        };
        reader.readAsArrayBuffer(file);
      } else {
        this.toolQRCodeBanner = null;
      }
    },

    validateNonFormFields() {
      if (!this.isEditMode && !this.isDuplicate && this.toolImageBytes === '') {
        this.$toast.warning(this.$t('errors.imageEmpty'), { timeout: 2500 });
        return false;
      }
      if (this.locationOption === '') {
        this.$toast.warning(this.$t('errors.locationEmpty'), { timeout: 2500 });
        return false;
      }
      if (this.toolTypeOption === '' || (this.toolTypeOption !== 'New Type' && !this.toolTypeOptions.find(op => op.type === this.toolTypeOption))) {
        this.$toast.warning(this.$t('errors.toolTypeEmpty'), { timeout: 2500 });
        return false;
      }
      if (this.enableToolReminders && !this.isEditMode) {
        for (const reminder of this.reminders) {
          if (!reminder.label || !reminder.label.trim()) {
            this.$toast.warning(this.$t('errors.reminderLabelEmpty'), { timeout: 2500 });
            return false;
          }
          if (!reminder.dueDate) {
            this.$toast.warning(this.$t('errors.reminderDateEmpty'), { timeout: 2500 });
            return false;
          }
        }
      }
      return true;
    },

    async saveOrUpdateTool() {
      this.isLoading = true;
      try {
        if (!this.validateNonFormFields()) {
          this.isLoading = false;
          return;
        }

        let toolTypeId = 0;
        if (this.newToolType !== '') {
          const newToolTypeResponse = await api.post('/ToolType', { type: this.newToolType });
          toolTypeId = newToolTypeResponse.data.id;
          this.$emit('refresh-tooltypesdata');
        } else {
          const foundType = this.toolTypeOptions.find(op => op.type === this.toolTypeOption);
          toolTypeId = foundType?.id || 0;
        }

        const foundLocation = this.locationOptions.find(op => op.address === this.locationOption);
        const locationId = foundLocation?.id || 0;

        const payload = {
          locationId,
          toolTypeId,
          name: this.name,
          description: this.description,
          toolImageBytes: this.toolImageBytes,
          purchaseDate: this.purchaseDate || null,
          brandName: this.brandName,
          isle: this.isle,
          shelf: this.shelf,
          purchasePrice: this.purchasePrice === "" ? 0 : Number(this.purchasePrice),
          dailyPrice: this.dailyPrice === "" ? 0 : Number(this.dailyPrice),
          sku: this.sku,
          maxReservationDays: this.maxReservationDays === "" ? 0 : Number(this.maxReservationDays),
          askForToolImageOnReturnForToolReservations: this.askForToolImageOnReturnForToolReservations,
          askForToolImageOnReturnForToolReservationsAfterDays: this.askForToolImageOnReturnForToolReservations
            ? Math.max(1, Math.floor(Number(this.askForToolImageOnReturnForToolReservationsAfterDays) || 1))
            : null,
          totalCount: this.totalCount === "" ? null : Number(this.totalCount),
          multiTool: this.multiTool,
          toolQRCodeBanner: this.toolQRCodeBanner,
          toolPriceTypeId: this.toolPriceTypeOption,
          erpItemCode: this.erpItemCode,
          deviceId: this.deviceId === '' ? null : this.deviceId,
          deviceTypeId: this.deviceTypeOption || null,
          // Reminders are only set at creation; on the tool page they are managed via their own endpoints
          reminders: !this.isEditMode && this.enableToolReminders ? this.reminders.map(r => ({
            label: r.label,
            dueDate: r.dueDate,
            notifyMonthBefore: r.notifyMonthBefore,
            notifyWeekBefore: r.notifyWeekBefore,
            notifyOnDate: r.notifyOnDate,
            recipientUserIds: r.recipientUserIds
          })) : null,
          ...(this.isEditMode && this.toolData ? { toolStatusId: this.toolData.toolStatusId } : {})
        };

        if (this.isEditMode) {
          await api.put(`/Tool/${this.toolId}`, payload);
          this.$toast.success(`${this.name} ${this.$t('common.updated')}`, { timeout: 2000 });
        } else {
          const response = await api.post('/Tool', payload);
          this.$toast.success(this.$t('tools.toolCreated'), { timeout: 2000 });
          if (this.isDuplicate) {
            this.$emit('navigate-to-tool', response.data);
          }
        }

        this.$emit('refresh-data');
        this.$emit('close');
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(getApiErrorMessage(this.$t, error), { timeout: 0 });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchFormData() {
      try {
        const [toolTypesResponse, locationsResponse, deviceTypesResponse, usersResponse] = await Promise.all([
          api.get('/ToolType'),
          api.get('/Location?pageSize=100'),
          api.get('/DeviceType'),
          api.get('/User', { params: { pageSize: 500, includeDisabled: false, excludeImages: true } })
        ]);
        this.toolTypeOptions = toolTypesResponse.data;
        this.locationOptions = locationsResponse.data.result;
        this.deviceTypeOptions = deviceTypesResponse.data;
        // /User returns a PaginatedList ({ result, totalItems, ... }) — unwrap .result
        this.userOptions = usersResponse.data?.result || [];

        // Re-match locationOption and toolTypeOption strings after options are loaded
        if ((this.isEditMode || this.isDuplicate) && this.toolData) {
          this.locationOption = this.toolData.location ? this.toolData.location.address : '';
          this.toolTypeOption = this.toolData.toolType ? this.toolData.toolType.type : '';
        }
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
  }
};
</script>

<style scoped>
.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #344767;
  margin-bottom: 0.5rem;
}

.form-check-label {
  font-size: 0.875rem;
  color: #344767;
}

.el-select {
  width: 100%;
}

:deep(.el-date-editor) {
  width: 100% !important;
}

:deep(.el-date-editor .el-input__wrapper) {
  border-radius: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

:deep(.dark-mode) .form-label,
:deep(.dark-mode) .form-check-label {
  color: rgba(255, 255, 255, 0.8);
}

:deep(.image-upload) {
  border: 2px dashed #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  transition: border-color 0.2s;
}

:deep(.image-upload:hover) {
  border-color: #7b809a;
}
</style>
